import { useState } from 'react'
import { ChevronDown, ChevronUp, Zap, AlertTriangle } from 'lucide-react'
import { DeadlineBanner } from './DeadlineBanner'
import { LeagueSelections } from './LeagueSelections'
import { getDisplayName, getTeamLogo, getShortName, isSameTeam } from '@/lib/teams'
import { isCompleted as isStatusCompleted, isLive as isStatusLive, isStopped as isStatusStopped } from '@/lib/fixture-status'
import type { MatchdayFixture } from '../types'

interface MatchdayWatchModeProps {
  matchdayNumber: number
  fixtures: MatchdayFixture[]
  teams?: { name: string; logo: string; code?: string }[]
  deadlineDate: Date | null
  isCompleted?: boolean
  countdown: {
    formatted: string
    isExpired: boolean
    hasDeadline: boolean
    totalSeconds: number
  }
  season: string
  redCards?: Record<string, { home: number; away: number }>
  currentPick?: {
    team_name: string
    used_double_down: boolean
    is_autopick?: boolean
  } | null
}

function formatKickoffShort(kickoff: string): string {
  const date = new Date(kickoff)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

  if (isToday) return time

  const dayStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${dayStr} ${time}`
}

function FixtureScoreCard({
  fixture,
  teams,
  highlightTeam,
  redCards,
}: {
  fixture: MatchdayFixture
  teams?: { name: string; logo: string; code?: string }[]
  highlightTeam?: string | null
  redCards?: { home: number; away: number }
}) {
  const isLive = isStatusLive(fixture.status)
  const isFinished = isStatusCompleted(fixture.status)
  const hasScore = fixture.home_score != null && fixture.away_score != null

  const homeLogo = getTeamLogo(fixture.home_team, teams)
  const awayLogo = getTeamLogo(fixture.away_team, teams)
  const homeShort = getShortName(fixture.home_team)
  const awayShort = getShortName(fixture.away_team)

  const homeDisplay = getDisplayName(fixture.home_team)
  const awayDisplay = getDisplayName(fixture.away_team)

  const isHomePick = highlightTeam ? isSameTeam(highlightTeam, fixture.home_team) : false
  const isAwayPick = highlightTeam ? isSameTeam(highlightTeam, fixture.away_team) : false
  const hasPick = isHomePick || isAwayPick

  // Determine result for picked team
  let pickResult: 'winning' | 'losing' | 'drawing' | null = null
  if (hasPick && hasScore) {
    if (isFinished && fixture.winner) {
      const pickedHome = isHomePick
      if ((pickedHome && fixture.winner === 'home') || (!pickedHome && fixture.winner === 'away')) pickResult = 'winning'
      else if (fixture.winner === 'draw') pickResult = 'drawing'
      else pickResult = 'losing'
    } else {
      const pickedScore = isHomePick ? fixture.home_score! : fixture.away_score!
      const opponentScore = isHomePick ? fixture.away_score! : fixture.home_score!
      if (pickedScore > opponentScore) pickResult = 'winning'
      else if (pickedScore < opponentScore) pickResult = 'losing'
      else pickResult = 'drawing'
    }
  }

  return (
    <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-colors ${
      isLive
        ? hasPick
          ? pickResult === 'winning'
            ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700'
            : pickResult === 'losing'
              ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700'
              : 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700'
          : 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800'
        : isFinished
          ? hasPick
            ? pickResult === 'winning'
              ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800'
              : pickResult === 'losing'
                ? 'bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                : 'bg-zinc-50 dark:bg-zinc-800/30 border-zinc-200 dark:border-zinc-700'
            : 'bg-zinc-50 dark:bg-zinc-800/30 border-zinc-200 dark:border-zinc-700'
          : 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700'
    }`}>
      {/* Home team */}
      <div className={`flex-1 flex items-center gap-2 justify-end ${isHomePick ? 'font-bold' : ''}`}>
        {redCards && redCards.home > 0 && (
          <span className="text-red-600 dark:text-red-400 text-[10px] font-bold shrink-0" title={`${redCards.home} red card${redCards.home > 1 ? 's' : ''}`}>
            🟥{redCards.home > 1 ? ` ×${redCards.home}` : ''}
          </span>
        )}
        <span className={`text-sm font-medium text-right truncate ${
          isHomePick ? 'text-zinc-900 dark:text-zinc-50' : 'text-zinc-700 dark:text-zinc-300'
        }`}>
          {homeShort}
        </span>
        {homeLogo.startsWith('http') ? (
          <img src={homeLogo} alt={homeDisplay} className="w-5 h-5 shrink-0 object-contain" />
        ) : (
          <span className="text-base shrink-0">{homeLogo}</span>
        )}
      </div>

      {/* Score */}
      <div className="flex items-center gap-1.5 shrink-0 min-w-[60px] justify-center">
        {hasScore ? (
          <>
            <span className={`text-lg font-mono font-bold tabular-nums w-6 text-center ${
              isLive ? 'text-emerald-700 dark:text-emerald-400' : 'text-zinc-900 dark:text-zinc-100'
            }`}>
              {fixture.home_score}
            </span>
            <span className="text-zinc-400 dark:text-zinc-500 text-xs">-</span>
            <span className={`text-lg font-mono font-bold tabular-nums w-6 text-center ${
              isLive ? 'text-emerald-700 dark:text-emerald-400' : 'text-zinc-900 dark:text-zinc-100'
            }`}>
              {fixture.away_score}
            </span>
          </>
        ) : (
          <span className="text-xs font-medium text-zinc-400 dark:text-zinc-500 text-center">
            {formatKickoffShort(fixture.kickoff_time)}
          </span>
        )}
      </div>

      {/* Away team */}
      <div className={`flex-1 flex items-center gap-2 ${isAwayPick ? 'font-bold' : ''}`}>
        {awayLogo.startsWith('http') ? (
          <img src={awayLogo} alt={awayDisplay} className="w-5 h-5 shrink-0 object-contain" />
        ) : (
          <span className="text-base shrink-0">{awayLogo}</span>
        )}
        <span className={`text-sm font-medium truncate ${
          isAwayPick ? 'text-zinc-900 dark:text-zinc-50' : 'text-zinc-700 dark:text-zinc-300'
        }`}>
          {awayShort}
        </span>
        {redCards && redCards.away > 0 && (
          <span className="text-red-600 dark:text-red-400 text-[10px] font-bold shrink-0" title={`${redCards.away} red card${redCards.away > 1 ? 's' : ''}`}>
            🟥{redCards.away > 1 ? ` ×${redCards.away}` : ''}
          </span>
        )}
      </div>

      {/* Status indicators — only rendered when there's a status to show */}
      {(() => {
        const status = fixture.status
        const elapsed = fixture.elapsed

        // Live statuses with pulsing dot
        if (status === 'LIVE' || status === '1H' || status === '2H' || status === 'ET' || status === 'P' || status === 'INT') {
          return (
            <div className="shrink-0 w-12 flex items-center justify-end gap-1">
              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                {elapsed ? `${elapsed}'` : status === 'P' ? 'PEN' : status}
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </div>
          )
        }

        // Halftime / break — amber, no dot
        if (status === 'HT' || status === 'BT') {
          return (
            <div className="shrink-0 w-12 flex items-center justify-end">
              <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                {status}
              </span>
            </div>
          )
        }

        // Finished statuses — muted gray
        if (isStatusCompleted(status)) {
          return (
            <div className="shrink-0 w-12 flex items-center justify-end">
              <span className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500">
                {status}
              </span>
            </div>
          )
        }

        // Postponed / cancelled / suspended / abandoned — red/warning
        if (isStatusStopped(status)) {
          const label = status === 'CANC' ? 'CAN' : status === 'SUSP' ? 'SUS' : status
          return (
            <div className="shrink-0 w-12 flex items-center justify-end">
              <span className="text-[10px] font-bold text-red-500 dark:text-red-400">
                {label}
              </span>
            </div>
          )
        }

        // NS / TBD / other — nothing (kickoff time shown in score area)
        return null
      })()}
    </div>
  )
}

export function MatchdayWatchMode({
  matchdayNumber,
  fixtures,
  teams,
  deadlineDate,
  isCompleted,
  countdown,
  season,
  redCards,
  currentPick,
}: MatchdayWatchModeProps) {
  const [showAllFixtures, setShowAllFixtures] = useState(false)

  // Categorize fixtures by status
  const liveFixtures = fixtures.filter(f => isStatusLive(f.status))
  const finishedFixtures = fixtures.filter(f => isStatusCompleted(f.status))
  const postponedFixtures = fixtures.filter(f => isStatusStopped(f.status))
  const scheduledFixtures = fixtures.filter(f =>
    !liveFixtures.includes(f) && !finishedFixtures.includes(f) && !postponedFixtures.includes(f)
  )

  // Order: live first, then scheduled (upcoming), then finished, then postponed
  const orderedFixtures = [...liveFixtures, ...scheduledFixtures, ...finishedFixtures, ...postponedFixtures]
  const visibleFixtures = showAllFixtures ? orderedFixtures : orderedFixtures.slice(0, 5)

  // Compute your pick's result if finished
  let pickResultInfo: { result: 'win' | 'draw' | 'loss' | 'pending'; score: string } | null = null
  if (currentPick) {
    const pickFixture = fixtures.find(f =>
      isSameTeam(f.home_team, currentPick.team_name) ||
      isSameTeam(f.away_team, currentPick.team_name)
    )
    if (pickFixture && pickFixture.home_score != null && pickFixture.away_score != null) {
      const isHome = isSameTeam(pickFixture.home_team, currentPick.team_name)
      const pickedScore = isHome ? pickFixture.home_score : pickFixture.away_score
      const opponentScore = isHome ? pickFixture.away_score : pickFixture.home_score

      const isFinished = isStatusCompleted(pickFixture.status)

      const isLive = isStatusLive(pickFixture.status)

      let result: 'win' | 'draw' | 'loss' | 'pending' = 'pending'
      if (isFinished) {
        const winner = pickFixture.winner
        if (winner) {
          if ((isHome && winner === 'home') || (!isHome && winner === 'away')) result = 'win'
          else if (winner === 'draw') result = 'draw'
          else result = 'loss'
        } else {
          if (pickedScore > opponentScore) result = 'win'
          else if (pickedScore < opponentScore) result = 'loss'
          else result = 'draw'
        }
      } else if (isLive) {
        // Show live status as pending but with score
        result = 'pending'
      }

      pickResultInfo = {
        result,
        score: `${pickFixture.home_score} - ${pickFixture.away_score}`,
      }
    }
  }

  const pickResultColors = {
    win: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800',
    draw: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
    loss: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800',
    pending: 'bg-zinc-50 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700',
  }

  const pickTextColors = {
    win: 'text-emerald-700 dark:text-emerald-300',
    draw: 'text-amber-700 dark:text-amber-300',
    loss: 'text-red-700 dark:text-red-300',
    pending: 'text-zinc-500 dark:text-zinc-400',
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <DeadlineBanner matchdayNumber={matchdayNumber} season={season} deadlineDate={deadlineDate} isCompleted={isCompleted} countdown={countdown} />

      {/* Status Banner */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
        liveFixtures.length > 0
          ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800'
          : scheduledFixtures.length === 0 && finishedFixtures.length > 0
            ? 'bg-zinc-100 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700'
            : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
      }`}>
        {liveFixtures.length > 0 ? (
          <>
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
              {liveFixtures.length} match{liveFixtures.length > 1 ? 'es' : ''} live
            </span>
          </>
        ) : scheduledFixtures.length === 0 && finishedFixtures.length > 0 ? (
          <span className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
            All matches complete{postponedFixtures.length > 0 ? ` · ${postponedFixtures.length} postponed` : ''}
          </span>
        ) : scheduledFixtures.length > 0 ? (
          <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
            Picks locked &middot; {scheduledFixtures.length} match{scheduledFixtures.length > 1 ? 'es' : ''} upcoming
          </span>
        ) : (
          <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
            Picks locked
          </span>
        )}
        {finishedFixtures.length > 0 && liveFixtures.length > 0 && (
          <>
            <span className="text-emerald-400 dark:text-emerald-600">&middot;</span>
            <span className="text-sm text-emerald-600 dark:text-emerald-400">
              {finishedFixtures.length} finished
            </span>
          </>
        )}
      </div>

      {/* Your Pick Summary */}
      {currentPick ? (
        <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
          pickResultInfo ? pickResultColors[pickResultInfo.result] : pickResultColors.pending
        }`}>
          <div>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
              Your pick
              {currentPick.is_autopick && (
                <span className="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-3 h-3" />
                  <span className="text-[10px] font-medium">Auto</span>
                </span>
              )}
            </p>
            <p className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-1.5 mt-0.5">
              {getDisplayName(currentPick.team_name)}
              {currentPick.used_double_down && (
                <span className="inline-flex items-center gap-0.5 text-xs font-bold text-violet-600 dark:text-violet-400">
                  <Zap className="w-3 h-3" /> DD
                </span>
              )}
            </p>
          </div>
          {pickResultInfo ? (
            <div className="text-right">
              <p className="text-sm font-mono font-medium text-zinc-600 dark:text-zinc-300">
                {pickResultInfo.score}
              </p>
              <p className={`text-xs font-semibold capitalize ${pickTextColors[pickResultInfo.result]}`}>
                {pickResultInfo.result === 'pending' ? 'In Progress' : pickResultInfo.result}
              </p>
            </div>
          ) : (
            <span className="text-sm text-zinc-400 dark:text-zinc-500 font-medium">
              {(() => {
                const pickFixture = fixtures.find(f =>
                  isSameTeam(f.home_team, currentPick.team_name) ||
                  isSameTeam(f.away_team, currentPick.team_name)
                )
                return pickFixture ? formatKickoffShort(pickFixture.kickoff_time) : 'Pending'
              })()}
            </span>
          )}
        </div>
      ) : (
        <div className="px-4 py-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
          <p className="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            No pick submitted for this matchday
          </p>
        </div>
      )}

      {/* Fixtures */}
      {fixtures.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Fixtures
          </h2>
          <div className="space-y-1.5">
            {visibleFixtures.map(fixture => (
              <FixtureScoreCard
                key={fixture.id}
                fixture={fixture}
                teams={teams}
                highlightTeam={currentPick?.team_name ?? null}
                redCards={redCards?.[fixture.id]}
              />
            ))}
          </div>
          {orderedFixtures.length > 5 && (
            <button
              onClick={() => setShowAllFixtures(!showAllFixtures)}
              className="flex items-center justify-center gap-1 w-full py-2 text-xs font-medium text-zinc-500 dark:text-zinc-400
                         hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
            >
              {showAllFixtures ? (
                <>Show fewer <ChevronUp className="w-3.5 h-3.5" /></>
              ) : (
                <>Show all {orderedFixtures.length} fixtures <ChevronDown className="w-3.5 h-3.5" /></>
              )}
            </button>
          )}
        </div>
      )}

      {/* League Selections */}
      <LeagueSelections matchday={matchdayNumber} season={season} />
    </div>
  )
}
